import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { pick, sum } from 'lodash';

import ChartItem from 'components/chart-item';
import TableItem from 'components/table-item';
import GeospatialItem from 'components/geospatial-item';
import D3 from 'components/d3';
import Error from 'components/error';
import { Column } from 'components/layout';
import LoadingState from 'components/loading-state';
import StatusBanner, {
  STATUS_BANNER_TYPE_ENUM,
} from 'components/status-banner';
import { DASHBOARD_ITEM_LOADING_TEXTS } from 'constants/dashboard-item';
import {
  FETCH_ITEM_DATA_ERRORS,
  FETCH_ITEM_DATA_WARNINGS,
} from 'constants/item-data-cache';
import { isTableType, isMapType, isD3Type } from 'constants/chart-roles';
import { MAX_COLUMN_LIMIT } from 'constants/table';
import getPipelineOptions from 'utils/get-pipeline-options';
import isObjectShallowEqual from 'utils/is-object-shallow-equal';

import styles from './chart.less';
import tableStyles from 'components/table-item/table-item.less';

class Chart extends Component {
  static propTypes = {
    className: PropTypes.string,
    fetchDataForItem: PropTypes.func.isRequired,
    item: PropTypes.object,
    isEditingLayout: PropTypes.bool,
    isHovering: PropTypes.bool,
    isLimiting: PropTypes.bool,
    isResizing: PropTypes.bool,
  };

  static defaultProps = {
    className: '',
    isHovering: false,
    isLimiting: false,
    isResizing: false,
  };

  shouldComponentUpdate(nextProps) {
    const {
      isEditingLayout,
      isHovering,
      isResizing,
      item: { chartType } = {},
    } = nextProps;

    // If we're hovering or moving items, do not rerender.
    if (isEditingLayout || isHovering) {
      return false;
    }

    if (isMapType(chartType)) {
      // Maps require knowledge of if they're resizing or not,
      // and need the isResizing prop to be relevant.
      // As a result, unlike the other chart types, we only consider
      // className and item.
      const propsToConsider = ['className', 'item'];
      const oldProps = pick(this.props, propsToConsider);
      const newProps = pick(nextProps, propsToConsider);

      return !isObjectShallowEqual(oldProps, newProps);
    }

    // If we're not dealing with a map, we should not render if we're resizing
    if (isResizing) {
      return false;
    }

    // isEditingLayout not considered since it is only applied when
    // the dashboard layout is being altered, the items themselves
    // are not changing.
    // We consider `isResizing` as the tile size will have changed,
    // which means that the inner item will need to rerender to fit
    const propsToConsider = ['className', 'isResizing', 'item'];
    const oldProps = pick(this.props, propsToConsider);
    const newProps = pick(nextProps, propsToConsider);

    // otherwise check if the props are equal
    return !isObjectShallowEqual(oldProps, newProps);
  }

  _renderError = () => {
    const { fetchDataForItem, item } = this.props;
    const { _id, dataSourceId, decoratedError } = item;
    const payload = {
      dataSourceId,
      dashboardItemId: _id,
      pipelineOptions: getPipelineOptions(item),
    };

    const error = decoratedError.error || {};

    // Further mutation here so telemetry is unaffected, and also errors are
    // collapsed by default for most chart users
    let errorMessage = decoratedError.errorMessage || '';
    if (errorMessage === error.message) {
      errorMessage = FETCH_ITEM_DATA_ERRORS.GENERAL_SERVER_ERROR;
    }

    // Stitch provides error.json, other errors usually have a message property
    const details =
      error.json || (error.message && { error: error.message }) || {};
    const retryVisible =
      errorMessage !== FETCH_ITEM_DATA_ERRORS.MISSING_DATASOURCE;
    const retryOnClick = fetchDataForItem.bind(null, payload);

    return (
      <div className={styles.error}>
        <Error
          details={details}
          retryOnClick={retryOnClick}
          retryVisible={retryVisible}
          statusMessage={errorMessage}
        />
      </div>
    );
  };

  _renderItemType = () => {
    const chartType = this.props.item.chartType;
    if (!chartType) {
      return null;
    }

    const { isEditMode } = this.props.item;
    const _className = isEditMode
      ? classnames(
          tableStyles['draggable-columns'],
          tableStyles['column-total']
        )
      : classnames(tableStyles['column-total']);

    if (isTableType(chartType)) {
      return <TableItem className={_className} {...this.props.item} />;
    }

    if (isMapType(chartType)) {
      return (
        <GeospatialItem
          {...this.props.item}
          isResizing={this.props.isResizing}
        />
      );
    }

    if (isD3Type(chartType)) {
      return <D3 {...this.props.item} />;
    }

    return <ChartItem {...this.props.item} />;
  };

  _renderStatusBanners = unrenderable => {
    const { chartType, isLimiting, data, spec } = this.props.item;

    const statusBanners = [];

    // Handling document limits
    const totalDocuments = data.documents.length;
    const maxDocLimit = data.metadata.maxDocLimit;

    if (chartType && totalDocuments >= maxDocLimit && !isLimiting) {
      statusBanners.push(
        <StatusBanner
          data-test-id="status-banner-max-response-size"
          key="status-banner-max-response-size"
          type={STATUS_BANNER_TYPE_ENUM.warning}
          muted
          isVisible
          isDismissable={false}
          gutter={false}
        >
          {// eslint-disable-next-line new-cap
          FETCH_ITEM_DATA_WARNINGS.MAX_RESPONSE_SIZE(chartType, maxDocLimit)}
        </StatusBanner>
      );
    }

    // Handling table status message
    if (isTableType(chartType)) {
      const totalTableColumns = sum(
        spec.columnDefs.map(col => col.children.length)
      );

      // Check if the total number of columns is renderable.
      // AgGrid has issues if the number of grouped columns is too large
      if (totalTableColumns > MAX_COLUMN_LIMIT) {
        statusBanners.push(
          <StatusBanner
            data-test-id="status-banner-too-many-columns"
            key="status-banner-too-many-columns"
            type={STATUS_BANNER_TYPE_ENUM.danger}
            muted
            isVisible
            isDismissable={false}
            gutter={false}
          >
            {`This table has ${totalTableColumns} columns, and cannot be rendered.`}
          </StatusBanner>
        );
        unrenderable();
      }
    }

    return statusBanners;
  };

  // eslint-disable-next-line complexity
  _renderItem = () => {
    const { decoratedError, title, isEditMode } = this.props.item;
    const errorMessage = (decoratedError && decoratedError.errorMessage) || '';

    let unrenderable = false;

    const statusBanners = this._renderStatusBanners(() => {
      unrenderable = true;
    });

    return (
      <Column
        data-test-id="chart"
        flex={1}
        justifyContent="flex-start"
        alignItems="stretch"
        className={styles.chart}
      >
        {!isEditMode && (
          <h4 data-test-id="chart-title" className={styles.title}>
            {title}
          </h4>
        )}
        {statusBanners}
        <Column
          flex={1}
          gutterLeft={false}
          gutterRight={false}
          gutterBottom={false}
          gutterTop={statusBanners.length > 0}
          justifyContent={errorMessage ? 'flex-start' : 'center'}
          alignItems={errorMessage ? 'stretch' : 'center'}
          className={styles['chart-content']}
        >
          {!unrenderable &&
            (errorMessage ? this._renderError() : this._renderItemType())}
        </Column>
      </Column>
    );
  };

  render() {
    const { isHovering, isEditingLayout, className } = this.props;
    const { isFetching, itemType, chartType, decoratedError } = this.props.item;
    const _className = classnames(
      styles.chart,
      {
        [styles['is-editing']]: isEditingLayout,
        [styles['is-fixed']]: !isEditingLayout,
        [styles['is-hovering']]: isHovering,
      },
      className
    );
    const loadingText =
      this.props.item.loadingText ||
      DASHBOARD_ITEM_LOADING_TEXTS[itemType] ||
      '';

    return (
      <Column
        justifyContent="center"
        alignItems="center"
        gutter={false}
        className={_className}
      >
        {isFetching && <LoadingState isBlocking text={loadingText} />}
        {(chartType || decoratedError) && this._renderItem()}
      </Column>
    );
  }
}

export default Chart;
export { Chart };
